import {FiAlertTriangle, FiEdit3, FiUserPlus, FiX} from "react-icons/fi";
import {useTranslation} from "react-i18next";
import PhoneInput from "../../../components/common/phoneInput/PhoneInput";
import Select from "../../../components/common/select/Select";
import styles from "./StudentFormModal.module.css";

const StudentFormModal = ({isOpen, mode, values, onChange, onClose, onSubmit, saving, error, basicOnly = false}) => {
    const {t} = useTranslation();
    if (!isOpen) return null;

    const isCreateMode = mode === "create";
    const title = isCreateMode ? t("pages.studentForm.createTitle") : t("pages.studentForm.editTitle");

    return (
        <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true"
             aria-labelledby="student-modal-title">
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.titleWrap}>
                        <span className={styles.headerIcon}>{isCreateMode ? <FiUserPlus size={22}/> :
                            <FiEdit3 size={22}/>}</span>
                        <h2 id="student-modal-title" className={styles.title}>{title}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose}
                            aria-label={t("pages.studentForm.closeAria")}>
                        <FiX size={20}/>
                    </button>
                </div>

                <form onSubmit={onSubmit} className={styles.form}>
                    <div className={styles.formBody}>
                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>{t("pages.studentForm.firstName")}</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={values.firstName}
                                    onChange={onChange}
                                    className={styles.input}
                                    placeholder={t("pages.studentForm.firstNamePlaceholder")}
                                    autoComplete="off"
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>{t("pages.studentForm.lastName")}</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={values.lastName}
                                    onChange={onChange}
                                    className={styles.input}
                                    placeholder={t("pages.studentForm.lastNamePlaceholder")}
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        {/*
                          * The email is not a contact detail here — it is the student's login. The initial
                          * password is mailed to it and the student signs in with it to sit the exam, so a
                          * junk address silently produces a student who can never take one (which is exactly
                          * what happened to a real centre). Shown in create mode only: editing a student
                          * changes the login address but re-sends no credentials.
                          */}
                        {isCreateMode && (
                            <div className={styles.emailNotice} role="note">
                                <FiAlertTriangle className={styles.emailNoticeIcon} size={18} aria-hidden="true"/>
                                <p className={styles.emailNoticeText}>
                                    <strong className={styles.emailNoticeTitle}>
                                        {t("pages.studentForm.emailNoticeTitle")}
                                    </strong>{" "}
                                    {t("pages.studentForm.emailNoticeText")}
                                </p>
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.studentForm.email")}</label>
                            <input
                                type="email"
                                name="email"
                                value={values.email}
                                onChange={onChange}
                                className={styles.input}
                                placeholder={t("pages.studentForm.emailPlaceholder")}
                                autoComplete="email"
                            />
                        </div>

                        {!basicOnly && (
                            <>
                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>{t("pages.studentForm.nationalId")}</label>
                                        <input
                                            type="text"
                                            name="nationalId"
                                            value={values.nationalId}
                                            onChange={onChange}
                                            className={styles.input}
                                            placeholder={t("pages.studentForm.nationalIdPlaceholder")}
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>{t("pages.studentForm.gender")}</label>
                                        <Select
                                            name="gender"
                                            value={values.gender}
                                            onChange={onChange}
                                            className={styles.select}
                                            options={[
                                                { value: "", label: t("pages.studentForm.genderSelect") },
                                                { value: "MALE", label: t("pages.studentForm.genderMale") },
                                                { value: "FEMALE", label: t("pages.studentForm.genderFemale") },
                                            ]}
                                        />
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t("pages.studentForm.birthDate")}</label>
                                    <input type="date" name="birthDate" value={values.birthDate} onChange={onChange}
                                           className={styles.input}/>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t("pages.studentForm.phone")}</label>
                                    <PhoneInput
                                        name="phone"
                                        value={values.phone || ""}
                                        onChange={(local) => onChange({target: {name: "phone", value: local}})}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t("pages.studentForm.address")}</label>
                                    <textarea
                                        name="address"
                                        value={values.address}
                                        onChange={onChange}
                                        className={styles.textarea}
                                        placeholder={t("pages.studentForm.addressPlaceholder")}
                                        rows={3}
                                    />
                                </div>
                            </>
                        )}

                        {error && (
                            <div className={styles.error} role="alert">
                                {error}
                            </div>
                        )}
                    </div>

                    <div className={styles.actions}>
                        <button type="button" onClick={onClose} className={styles.secondaryBtn}>
                            {t("common.cancel")}
                        </button>
                        <button type="submit" disabled={saving} className={styles.primaryBtn}>
                            {saving ? t("pages.studentForm.saving") : isCreateMode ? t("pages.studentForm.addBtn") : t("pages.studentForm.saveBtn")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StudentFormModal;
